import {useMutation, useQuery} from '@apollo/client';
import {Box} from '@mui/material';
import React, {useEffect, useRef, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useSnackbar} from 'notistack';
import {Panel} from '../../components/Panel/Panel';
import {useQueryParams} from '../../hooks/useQueryParams';
import {
  BusinessUnitEnum,
  OneViewModelTypesEnum,
  ReviewStatusOneviewEnum,
} from '../../schema-types/globalTypes';
import {
  GetNativeIdFromCuro,
  GetNativeIdFromCuroVariables,
} from '../../schema-types/GetNativeIdFromCuro';
import {
  CREATE_REVIEW,
  GET_ACCOUNT_BASIC,
  GET_ACCOUNT_REVIEWS,
} from '../../components/AccountOverview/accountOverviewQueries';
import {CreateReview, CreateReviewVariables} from '../../schema-types/CreateReview';
import {AccountReviews, AccountReviewsVariables} from '../../schema-types/AccountReviews';
import {
  SingleAccountBasic,
  SingleAccountBasicVariables,
} from '../../schema-types/SingleAccountBasic';
import {GET_NATIVE_ID_FROM_CURO} from './TestHomepage';
import {useGetSppReview} from '../AccountInstructionsPage/AccountInstructionsPage';
import validateUUID from '../../utils/validateUUID';
import {useRequirementsDentistOrBAU} from '../../hooks/useRequirementsDentistOrBAU';

/**
 * Where the test-setup page sends the user once a review is found/created. SPP DA accounts
 * open the account-level instruction builder (no review-journey); AR/SFP accounts open the
 * review-journey recommendation builder. New-vs-old (builder vs recommends-builder) is
 * already encoded in `createRecommendUrl` (from useRequirementsDentistOrBAU).
 */
export const resolveTestReviewDestination = ({
  nativeId,
  reviewId,
  partnershipType,
  createRecommendUrl,
}: {
  nativeId: string;
  reviewId: string;
  partnershipType: BusinessUnitEnum | null | undefined;
  createRecommendUrl: string;
}): string =>
  partnershipType === BusinessUnitEnum.DA
    ? `/account/${nativeId}/${createRecommendUrl}`
    : `/account/${nativeId}/review-journey/${reviewId}/${createRecommendUrl}`;

export const describeTestSetup = ({
  partnershipType,
  hasDentistPerm,
}: {
  partnershipType: BusinessUnitEnum | null | undefined;
  hasDentistPerm: boolean;
}): string => {
  const account =
    partnershipType === BusinessUnitEnum.DA
      ? 'SPP DA'
      : partnershipType === BusinessUnitEnum.AR
      ? 'SPP AR'
      : partnershipType === BusinessUnitEnum.SFP
      ? 'SFP'
      : 'Unknown';
  return `Account: ${account} · opening ${
    hasDentistPerm ? 'dentist builder' : 'BAU flow'
  }`;
};

export const TestAnyOpenReviewPage: React.FC = () => {
  const {accountCuroId} = useParams();
  const [loading, setLoading] = useState(true);
  const params = useQueryParams();
  const curoId = validateUUID(accountCuroId);
  const {data: accountData, loading: accountLoading} = useQuery<
    GetNativeIdFromCuro,
    GetNativeIdFromCuroVariables
  >(GET_NATIVE_ID_FROM_CURO, {
    variables: {
      curoId: curoId!,
      modelName: OneViewModelTypesEnum.ACCOUNT,
    },
    skip: !curoId,
  });
  const [createReview, {loading: createReviewLoading, data: createReviewData}] =
    useMutation<CreateReview, CreateReviewVariables>(CREATE_REVIEW);
  const {loading: reviewsLoading, data: reviewsData} = useQuery<
    AccountReviews,
    AccountReviewsVariables
  >(GET_ACCOUNT_REVIEWS, {
    variables: {
      accountId: validateUUID(accountData?.nativeId)!,
      idType: 'id',
      onlyCurrent: true,
    },
    skip: !validateUUID(accountData?.nativeId),
  });
  const {loading: accountBasicLoading, data: accountBasicData} = useQuery<
    SingleAccountBasic,
    SingleAccountBasicVariables
  >(GET_ACCOUNT_BASIC, {
    variables: {
      accountId: validateUUID(accountData?.nativeId)!,
      curoSync: false,
    },
    skip: !validateUUID(accountData?.nativeId),
  });
  const partnershipType =
    accountBasicData?.accountDetails?.businessUnit?.partnershipType ?? null;

  const {createRecommendUrl} = useRequirementsDentistOrBAU();
  const {enqueueSnackbar} = useSnackbar();
  // `builder` is the dentist experience; `recommends-builder` is the BAU one.
  const hasDentistPerm = createRecommendUrl === 'builder';
  const hasRunRef = useRef(false);

  useEffect(() => {
    async function go() {
      const nativeId = accountData?.nativeId;
      // Wait until we know the review state and the business unit before deciding, so we
      // neither create a review prematurely nor route before the account type is known.
      if (!nativeId || reviewsLoading || accountBasicLoading || hasRunRef.current) {
        return;
      }
      hasRunRef.current = true;

      const reviewName = params.get('reviewName');
      enqueueSnackbar(describeTestSetup({partnershipType, hasDentistPerm}), {
        variant: 'info',
      });

      const review = reviewsData?.reviews?.find(
        (r) => r.statusOneview === ReviewStatusOneviewEnum.OPEN
      );
      if (review?.id) {
        navigate(
          {
            pathname: resolveTestReviewDestination({
              nativeId,
              reviewId: review.id,
              partnershipType,
              createRecommendUrl,
            }),
          },
          {replace: true} // so we dont have this page in the browser history
        );
        return;
      }

      try {
        const createReviewResult = await createReview({
          variables: {
            accountId: nativeId,
            reviewType: 'Ad-hoc',
            name: reviewName ? `Test: ${reviewName}` : 'Test review',
            ignoreLimit: true,
          },
        });
        const newReviewId = createReviewResult.data?.createReview?.review?.id;
        if (newReviewId) {
          navigate(
            {
              pathname: resolveTestReviewDestination({
                nativeId,
                reviewId: newReviewId,
                partnershipType,
                createRecommendUrl,
              }),
            },
            {replace: true}
          );
        }
      } finally {
        setLoading(false);
      }
    }
    go();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    reviewsData?.reviews,
    accountData?.nativeId,
    reviewsLoading,
    accountBasicLoading,
    partnershipType,
    createRecommendUrl,
  ]);
  const navigate = useNavigate();
  return (
    <div>
      <Panel title="Account test setup">
        Curo id: {accountCuroId}
        {reviewsLoading || reviewsLoading || loading ? <Box>Setting up test</Box> : null}
      </Panel>
    </div>
  );
};

export const TestInstruction: React.FC = () => {
  const {accountCuroId} = useParams();
  const [loading, setLoading] = useState(true);
  const params = useQueryParams();
  const curoId = validateUUID(accountCuroId);
  const {data: accountData, loading: accountLoading} = useQuery<
    GetNativeIdFromCuro,
    GetNativeIdFromCuroVariables
  >(GET_NATIVE_ID_FROM_CURO, {
    variables: {
      curoId: curoId!,
      modelName: OneViewModelTypesEnum.ACCOUNT,
    },
    skip: !curoId,
  });
  return (
    <div>
      {accountData?.nativeId ? (
        <TestInstructionInner accountId={accountData.nativeId} />
      ) : (
        <Panel title="Account test setup">
          {loading ? <Box>Setting up test</Box> : null}
        </Panel>
      )}
    </div>
  );
};

export const TestInstructionInner: React.FC<{accountId: string}> = ({accountId}) => {
  const {loading: reviewLoading, reviewId} = useGetSppReview(accountId);
  const {createRecommendUrl} = useRequirementsDentistOrBAU();
  useEffect(() => {
    async function go() {
      if (!reviewId) {
        return;
      }

      navigate(
        {
          pathname: `/account/${accountId}/${createRecommendUrl}`,
        },
        {
          replace: true,
        }
      );
    }

    go();
  }, [accountId, reviewId]);
  const navigate = useNavigate();
  return (
    <div>
      <Panel title="Account test setup">
        {reviewLoading ? <Box>Setting up test</Box> : null}
      </Panel>
    </div>
  );
};
